import { Widget } from '../../core/Widget.js';
import { ImViewBridge } from '../../legacy/ImViewBridge.js';
import { Loc } from '../../locales/registry.js';
import { parseBraceOrCsv } from '../../core/EncodeValues.js';
import { CmdBus, CmdPanelWidget } from '../CmdPanelWidget.js';
import { DomReaper } from '../../core/DomReaper.js';
import { ItemsCmdController } from '../../core/items/ItemsCmdController.js';
import { CollectionServerCmd } from './ICollectionWidget.js';
import { CollectionItemsController } from './CollectionItemsController.js';
import { ViewWidget } from '../../view/ViewWidget.js';
import { ViewWidgetHelper } from '../../view/ViewWidgetHelper.js';
import { ViewCmdType } from '../../view/IViewWidget.js';
import { ImSys } from '../../core/ImSys.js';
import { findFirstBySearchMask } from '../../core/imcmd/SearchMask.js';

const DEFAULT_OPTS = {
    selectmode: 'multiselect',
    sortable: false,
    cmdpanel: false,
    allows: '',
    checked: ''
};
/**
 * Порт jQuery‑виджета imCollection на чистый TypeScript без jQuery.
 *
 * ⚠️ ВАЖНО: виджет НЕ читает data‑атрибуты. Все параметры приходят через опции (lowercase).
 */
class CollectionWidget extends Widget {
    constructor() {
        super(...arguments);
        this._cmdPanelOwned = false;
        this._checkedCount = 0;
        this._totalCount = 0;
        this._scrollPage = 1;
        this._scrollRequested = false;
        this._scrollDisabled = false;
        this._firstCheckedItem = null;
        this._unbinders = [];
        this._inputUnbinders = [];
        this._typeUnbinders = [];
        this._lastTypeReloadValue = '';
        this._valueKey = ''; // из options.checked
        this._selectionByCheckbox = false; // автоопределяется по DOM
    }
    /** CSS‑ключ для автоинициализации через реестр/классы */
    static _cssKey() { return 'im-collection'; }
    // === API совместимости ===
    /** Вернуть число отмеченных элементов */
    getCheckedCount() { return this._checkedCount; }
    /** Вернуть общее число элементов */
    getTotalCount() { return this._totalCount; }
    /** Инициализация виджета: подготавливает DOM, события, панель, сортировку. */
    _create(op) {
        this._opts = { ...DEFAULT_OPTS, ...op };
        // radio → single как и было
        if (this._opts.selectmode === 'radio')
            this._opts.selectmode = 'single';
        // ❶ valueKey (легаси: options.checked)
        this._valueKey = (this._opts.checked ?? '').trim();
        // ❷ есть ли чекбоксы выбора?
        this._selectionByCheckbox = !!this.element.querySelector('[data-key="isChecked"]');
        // Инициализация счётчиков
        this._totalCount = this.queryItems().length;
        // + подключаем контроллер предметных операций коллекции
        this._itemsCtrl = new CollectionItemsController(this);
        this.items = new ItemsCmdController({
            selection: this._itemsCtrl, // выбор/выделение
            crud: this._itemsCtrl, // удаление выбранных / удаление по id
            // folders/order для коллекции сейчас не поддерживаем
        });
        this._initCmdPanel();
        // Подписки на чекбоксы выбора — ТОЛЬКО если они есть и selectmode активен
        this._rebindCheckInputs();
        this._rebindTypeReload();
        // Делегированные клики по элементам — как было, но внутри учитываем _selectionByCheckbox
        if (this._opts.selectmode !== 'none' && this._opts.selectmode !== 'onlyCheckbox') {
            // chkarea имеет приоритет
            this._delegate('click', '.im-item .chkarea', (e, target) => {
                const origin = e.target;
                // если кликнули по интерактиву — НЕ мешаем (чекбокс сам переключится и даст change)
                if (origin.closest('input,textarea,select,button,a,label,[data-noselect]'))
                    return;
                e.preventDefault();
                const item = target.closest('.im-item');
                if (item)
                    this._onItemClick(item, !!e.shiftKey);
            });
            this._notMask = ViewWidget.closestView(this.element)?.getUiNotMask() || '';
            const clickableSelector = `.im-item${this._notMask}`;
            // клики по item — только если у item НЕТ chkarea
            this._delegate('click', clickableSelector, (e, item) => {
                if (e.target.closest('a,button,[data-noselect]'))
                    return;
                if (item.querySelector('.chkarea'))
                    return; // приоритет у chkarea
                this._onItemClick(item, e.shiftKey);
            });
        }
        // Автоподгрузка при прокрутке
        if (this._opts.autoloadmargin !== undefined) {
            const onScroll = () => this._checkAutoload();
            window.addEventListener('scroll', onScroll);
            this._unbinders.push(() => window.removeEventListener('scroll', onScroll));
        }
        // HTML5 сортировка
        if (this._opts.sortable)
            this._enableHtml5Sort();
        // 2026-04-11: первичная инициализация не должна триггерить change/imOnChange,
        // иначе view с imOnChange="reload" уходит в цикл.
        this.recountAndEmit(false);
        this._reflectTotalMask();
        // Общее количество результатов (опционально)
        const totalText = (op.resulttotal ?? "").trim();
        const totalMask = op.resulttotalmask?.trim();
        if (totalText !== "" && totalMask) {
            const node = findFirstBySearchMask(this.element, totalMask);
            if (node)
                node.textContent = totalText;
        }
    }
    // === Публичные методы ===
    /** Получить все выбранные строки */
    getSelected() {
        const selected = [];
        this.queryItems('.checked').forEach((it) => {
            const val = this.getItemVal(it);
            if (val != null)
                selected.push(val);
        });
        return selected;
    }
    /** Обновить только содержимое коллекции без реинициализации виджета */
    reloadComplete(html) {
        if (typeof html !== 'string')
            return;
        const tpl = document.createElement('template');
        tpl.innerHTML = html.trim();
        const nodes = Array.from(tpl.content.childNodes);
        this.element.replaceChildren(...nodes);
        const htmlElements = nodes.filter((n) => n instanceof HTMLElement);
        const view = ViewWidget.closestView(this.element);
        view?.dom.setXClass();
        view?.initHtml(htmlElements, true, false);
        ImSys.initDomMany(htmlElements);
        this._selectionByCheckbox = !!this.element.querySelector('[data-key="isChecked"]');
        this._rebindCheckInputs();
        this._rebindTypeReload();
        this._setItemsDraggable(this.queryItems());
        this._firstCheckedItem = null;
        this._scrollPage = 1;
        this._scrollRequested = false;
        this._scrollDisabled = false;
        this._totalCount = this.queryItems().length;
        this._reflectTotalMask();
        this.recountAndEmit(false);
    }
    /** Нажатие кнопки панели команд: эмитит событие в .im-view и ждёт promise‑ответ */
    _onCmdPanelClick(buttonCmd) {
        const selected = this.getSelected();
        this._itemsCtrl.exec(buttonCmd, { rowindexes: selected.join(',') });
    }
    // === Обработчики ===
    /** Синхронизация DOM-состояния выбранности без эмита событий */
    _syncItemCheckedState(input) {
        const item = input.closest('.im-item');
        if (item)
            this.setItemChecked(item, input.checked); // синхроним класс
        const view = input.closest('.im-view');
        if (view)
            view.classList.toggle('checked', input.checked);
    }
    /** Реакция на чекбокс выбранности: обновляет .checked на элементе и на .im-view, пересчитывает */
    _onItemCheckedChanged(input) {
        this._syncItemCheckedState(input);
        this.recountAndEmit();
    }
    /** Клик по элементу: логика одиночного/мультивыбора и Shift‑диапазона */
    _onItemClick(item, shift) {
        if (this._opts.selectmode === 'none')
            return;
        const isOn = item.classList.contains('checked');
        const set = (on) => this.setItemChecked(item, on);
        if (isOn && !shift) {
            set(false);
            if (this._firstCheckedItem === item)
                this._firstCheckedItem = null;
        }
        else {
            if (this._opts.selectmode !== 'multiselect') {
                this.queryItems('.checked').forEach(i => this.setItemChecked(i, false));
                set(true);
            }
            else if (shift && this._firstCheckedItem) {
                const items = this.queryItems();
                const a = items.indexOf(this._firstCheckedItem);
                const b = items.indexOf(item);
                if (a >= 0 && b >= 0) {
                    const [from, to] = a <= b ? [a, b] : [b, a];
                    items.forEach(i => this.setItemChecked(i, false));
                    for (let i = from; i <= to; i++)
                        this.setItemChecked(items[i], true);
                }
                try {
                    window.getSelection?.()?.removeAllRanges();
                }
                catch { }
            }
            else {
                set(true);
                this._firstCheckedItem = item;
            }
        }
        this.recountAndEmit();
    }
    // === Служебные ===
    /** Найти все элементы коллекции (по селектору .im-item) */
    queryItems(filter = '') {
        return Array.from(this.element.querySelectorAll(`.im-item${filter}`));
    }
    /** Найти чекбоксы выбранности по ключу checkedkey */
    _queryCheckInputs(root = this.element) {
        return Array.from(root.querySelectorAll('[data-key="isChecked"]'));
    }
    /** Перепривязка обработчиков чекбоксов после полной подмены контента */
    _rebindCheckInputs() {
        this._clearInputBindings();
        this._bindCheckInputs(this.element);
    }
    /** Снять все ранее навешенные обработчики чекбоксов */
    _clearInputBindings() {
        for (const u of this._inputUnbinders) {
            try {
                u();
            }
            catch { }
        }
        this._inputUnbinders = [];
    }
    /** Навесить обработчики на чекбоксы в указанной области */
    _bindCheckInputs(root) {
        if (this._opts.selectmode === 'none' || !this._selectionByCheckbox)
            return;
        this._queryCheckInputs(root).forEach(input => {
            this._syncItemCheckedState(input); // первичная синхронизация без change
            const onChange = () => this._onItemCheckedChanged(input);
            input.addEventListener('change', onChange);
            this._inputUnbinders.push(() => input.removeEventListener('change', onChange));
        });
    }
    /** Получить поле текстового поиска по маске textmask */
    _resolveTypeReloadInput() {
        const mask = (this._opts.textmask ?? '').trim();
        if (!mask)
            return null;
        const host = findFirstBySearchMask(this.element, mask);
        if (!host)
            return null;
        if (host instanceof HTMLInputElement || host instanceof HTMLTextAreaElement) {
            return host;
        }
        // Возможно маска указывает на контейнер (обертку), внутри которого лежит input/textarea
        return host.querySelector('input,textarea');
    }
    /** Снять подписки для type-reload */
    _clearTypeReloadBindings() {
        for (const u of this._typeUnbinders) {
            try {
                u();
            }
            catch { }
        }
        this._typeUnbinders = [];
        if (this._typeReloadTimer != null) {
            window.clearTimeout(this._typeReloadTimer);
            this._typeReloadTimer = undefined;
        }
    }
    /** Перепривязать обработчик type-reload */
    _rebindTypeReload() {
        this._clearTypeReloadBindings();
        const input = this._resolveTypeReloadInput();
        if (!input)
            return;
        this._lastTypeReloadValue = input.value ?? '';
        const onInput = () => {
            if (this._typeReloadTimer != null) {
                window.clearTimeout(this._typeReloadTimer);
            }
            this._typeReloadTimer = window.setTimeout(() => {
                this._typeReloadTimer = undefined;
                this._reloadByTypeInput(input);
            }, 700);
        };
        const onChange = () => {
            if (this._typeReloadTimer != null) {
                window.clearTimeout(this._typeReloadTimer);
                this._typeReloadTimer = undefined;
            }
            this._reloadByTypeInput(input);
        };
        input.addEventListener('input', onInput);
        input.addEventListener('change', onChange);
        this._typeUnbinders.push(() => input.removeEventListener('input', onInput));
        this._typeUnbinders.push(() => input.removeEventListener('change', onChange));
    }
    /** Debounce-trigger reload по текстовому полю; игнорирует повторы значения */
    _reloadByTypeInput(input) {
        const nextVal = input.value ?? '';
        if (nextVal === this._lastTypeReloadValue)
            return;
        this._lastTypeReloadValue = nextVal;
        this._itemsCtrl.reloadAll();
    }
    /** Получить значение элемента (из инпута по checkedkey либо data-val) */
    getItemVal(item) {
        if (this._valueKey) {
            const inp = item.querySelector(`[data-key="${this._valueKey}"]`);
            if (inp?.value != null)
                return String(inp.value);
        }
        return item.getAttribute('data-val');
    }
    /** Установить/снять выделение элемента + синхронизировать чекбокс */
    setItemChecked(item, on) {
        item.classList.toggle('checked', on);
        if (this._selectionByCheckbox) {
            const cb = item.querySelector('[data-key="isChecked"]');
            if (cb && cb.checked !== on)
                cb.checked = on;
        }
    }
    /** Пересчитать выбранные, обновить маски, опционально дернуть change и панель */
    recountAndEmit(emitChange = true) {
        this._checkedCount = this.queryItems('.checked').length;
        const mask = this._opts.checkedcountmask?.trim();
        if (mask) {
            const node = findFirstBySearchMask(this.element, mask);
            if (node)
                node.textContent = String(this._checkedCount);
        }
        if (emitChange) {
            // локальный change на самой коллекции
            this.element.dispatchEvent(new Event('change'));
        }
        if (emitChange) {
            ImViewBridge.execEvent(null, 'collection-check-changed');
        }
        this.panel?.setSelection(this._checkedCount);
    }
    /** Обновить счётчик элементов */
    _reflectTotalMask() {
        const mask = this._opts.totalmask?.trim();
        if (!mask)
            return;
        const node = findFirstBySearchMask(this.element, mask);
        if (node)
            node.textContent = String(this._totalCount);
    }
    /** Найти ближайший контейнер .im-view */
    _closestView() { return this.element.closest('.im-view'); }
    // === Панель команд ===
    /** Создать/подключить панель команд, навесить обработчики */
    _initCmdPanel() {
        const mode = this._opts.cmdpanel;
        if (!mode)
            return;
        // 1) где рисовать/гидратировать панель
        let host = null;
        if (mode === true) {
            const prev = this.element.previousElementSibling;
            host = (prev?.classList.contains('im-toolstrip') ? prev : null) ?? (() => {
                const div = document.createElement('div');
                div.className = 'im-toolstrip';
                this.element.parentElement?.insertBefore(div, this.element);
                this._cmdPanelOwned = true;
                return div;
            })();
        }
        else {
            host = this._closestView()?.querySelector(mode) ?? null;
        }
        if (!host)
            return;
        // 2) cmd bus с теми же действиями, что были
        const bus = new CmdBus();
        bus.on("reload", () => this._itemsCtrl.reloadAll(this.getSelected()));
        bus.on("delete", () => this._onCmdPanelClick(CollectionServerCmd.delete_rows), {
            text: Loc.alertDeleteItems,
            title: Loc.askDeleteCollectionItems
        });
        // 3) создаем/гидратируем панель
        this.panel = CmdPanelWidget.getOrInit(host, {
            cmdBus: bus,
            allow: this._opts.allows,
            filterButton: false, // коллекции не нужен тумблер фильтра
            pagination: false, // у нас autoload, не страничная навигация
            addMissingButtons: true // дорисуем недостающие кнопки при кастомной разметке
        });
        // начальная синхронизация состояния
        this.panel.setSelection(this._checkedCount);
    }
    // === Autoload ===
    /**
     * Проверка на достижение нижнего отступа и запрос следующей страницы.
     * Ожидает, что .im-view обработает событие `imview:execCmd` и вернёт {html, end} через resolve().
     */
    _checkAutoload() {
        if (this._opts.autoloadmargin == null || this._scrollRequested || this._scrollDisabled)
            return;
        const bottomMargin = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
        if (bottomMargin > this._opts.autoloadmargin)
            return;
        this._scrollRequested = true;
        const loading = document.createElement('div');
        loading.className = 'autoloading';
        loading.textContent = Loc.dataLoading;
        this.element.append(loading);
        const nextPage = this._scrollPage + 1;
        ViewWidgetHelper.ExecCmd(this.element, {
            cmdType: ViewCmdType.itemcmd,
            cmd: CollectionServerCmd.autoload,
            options: { page: nextPage },
            onSuccess: (data) => {
                loading.remove();
                try {
                    this._autoloadInsertResult(nextPage, data?.html ?? '', !!data?.end);
                }
                catch (e) {
                    console.error(e);
                }
                this._scrollRequested = false;
            },
            onError: (err) => {
                loading.remove();
                this._scrollRequested = false;
                console.error('error on autoload', err);
            }
        });
    }
    /** Вставить подгруженные элементы, оповестить .im-view и обновить счётчики */
    _autoloadInsertResult(newPage, html, end) {
        if (this._scrollPage + 1 !== newPage) {
            console.error(`пришла не правильная страница ${newPage} ожидалась ${this._scrollPage + 1}`);
            return;
        }
        const tpl = document.createElement('template');
        tpl.innerHTML = html.trim();
        const newEls = Array.from(tpl.content.children);
        // сначала реально вставляем в DOM
        newEls.forEach(el => this.element.appendChild(el));
        // Инициализация добавленного HTML
        const view = ViewWidget.closestView(this.element);
        view?.dom.setXClass();
        view?.initHtml(newEls, true, false);
        ImSys.initDomMany(newEls);
        // как и раньше — снять временные классы, обновить состояние
        const newElements = Array.from(this.element.querySelectorAll('.autoloaded-item'));
        newElements.forEach(e => e.classList.remove('autoloaded-item'));
        this._scrollPage = newPage;
        if (end)
            this._scrollDisabled = true;
        this.element.dispatchEvent(new CustomEvent('im-collection-autoloaded'));
        newEls.forEach(el => this._bindCheckInputs(el));
        this._setItemsDraggable(newEls);
        this._totalCount = this.queryItems().length;
        this._reflectTotalMask();
    }
    // === Sortable (HTML5) ===
    /**
     * Включает нативный DnD для .im-item и диспатчит событие `imview:change-value` с именем из sortableupdate.
     * Реализация упрощённая: перетаскивание в рамках одного контейнера.
     */
    _enableHtml5Sort() {
        const list = this.element;
        let dragEl = null;
        const onDragStart = (e) => { const item = e.target?.closest('.im-item'); if (!item)
            return; dragEl = item; item.classList.add('dragging'); e.dataTransfer?.setData('text/plain', ''); e.dataTransfer?.setDragImage(item, 10, 10); };
        const onDragEnd = () => { dragEl?.classList.remove('dragging'); dragEl = null; };
        const onDragOver = (e) => { if (!dragEl)
            return; const after = this._getDragAfterElement(list, e.clientY); e.preventDefault(); if (!after)
            list.appendChild(dragEl);
        else
            list.insertBefore(dragEl, after); };
        this._delegate('dragstart', '.im-item', onDragStart);
        this._delegate('dragend', '.im-item', onDragEnd);
        list.addEventListener('dragover', onDragOver);
        this._unbinders.push(() => list.removeEventListener('dragover', onDragOver));
        this._setItemsDraggable(this.queryItems());
        if (this._opts.sortableupdate) {
            const onDrop = () => {
                ViewWidgetHelper.ExecItemCmd(this.element, {
                    cmd: this._opts.sortableupdate
                });
            };
            this.element.addEventListener('dragend', onDrop);
            this._unbinders.push(() => this.element.removeEventListener('dragend', onDrop));
        }
    }
    /** Проставить draggable только если сортировка включена */
    _setItemsDraggable(items) {
        if (!this._opts.sortable)
            return;
        items.forEach(i => i.setAttribute('draggable', 'true'));
    }
    /** Найти элемент после которого надо вставлять во время перетаскивания */
    _getDragAfterElement(container, y) {
        const els = Array.from(container.querySelectorAll('.im-item:not(.dragging)'));
        let closest = {
            offset: Number.NEGATIVE_INFINITY,
            el: null
        };
        for (const el of els) {
            const box = el.getBoundingClientRect();
            const offset = y - (box.top + box.height / 2);
            if (offset < 0 && offset > closest.offset)
                closest = {
                    offset,
                    el
                };
        }
        return closest.el;
    }
    /** Делегировать событие от контейнера к потомкам по селектору */
    _delegate(type, selector, handler) {
        const on = (e) => {
            const t = e.target.closest(selector);
            if (!t || !this.element.contains(t))
                return;
            handler(e, t);
        };
        this.element.addEventListener(type, on);
        this._unbinders.push(() => this.element.removeEventListener(type, on));
    }
    /**
       * Собирает значения в формате легаси EncodeValues:
       *  dataKey -> "{id1}{id2}..."
       *  dataKey__count -> N
       *  dataKey__sort -> "{s1}{s2}..."
       *
       * @param rKeyOpt опционально: базовый ключ.
       *  Если не передан — берём из data-key текущего контейнера.
       */
    getValues() {
        const dataKey = this.element.dataset.key?.trim();
        if (!dataKey)
            return undefined;
        const out = {};
        const selectedVals = [];
        // выбранные элементы
        this.queryItems('.checked').forEach(it => {
            const v = this.getItemVal(it);
            if (v != null)
                selectedVals.push(v);
        });
        // ── совместимость: если нет выбора и сортировка не включена → ключ есть, значение null
        if (selectedVals.length === 0 && !this._opts.sortable) {
            out[dataKey] = null;
            return out;
        }
        // иначе заполняем values + count
        out[dataKey] = selectedVals;
        out[`${dataKey}__count`] = selectedVals.length;
        // __sort только при sortable=true
        if (this._opts.sortable) {
            const allVals = [];
            this.queryItems().forEach(it => {
                const v = this.getItemVal(it);
                if (v != null)
                    allVals.push(v);
            });
            out[`${dataKey}__sort`] = allVals;
        }
        return out;
    }
    /**
    * Проставляет отмеченные элементы из values (только часть про выбор).
    * Ожидает строку формата "{id1}{id2}..." по ключу dataKey или массив строк [ id1, id2 ].
    * dataKey берём из аргумента или из data-key контейнера.
    */
    setValues(values) {
        const dataKey = this.element.dataset.key?.trim();
        if (!dataKey)
            return true;
        const raw = values[dataKey];
        if (raw === undefined)
            return false;
        const wanted = new Set(parseBraceOrCsv(raw).map(String));
        let firstStillChecked = false;
        this.queryItems().forEach(it => {
            const v = this.getItemVal(it);
            const on = v != null && wanted.has(v);
            this.setItemChecked(it, on);
            if (on && it === this._firstCheckedItem)
                firstStillChecked = true;
        });
        if (!firstStillChecked)
            this._firstCheckedItem = null;
        this.recountAndEmit(false); // чтобы маски/панель обновились без change
        return true;
    }
    /** Очистка ресурсов: отписка от всех слушателей/ссылок */
    _onDestroy() {
        this._clearTypeReloadBindings();
        this._clearInputBindings();
        for (const u of this._unbinders)
            try {
                u();
            }
            catch { }
        this._unbinders = [];
        if (this.panel?.element) {
            DomReaper.remove(this.panel.element, this.panel);
        }
        if (this._cmdPanelOwned && this.panel?.element?.isConnected) {
            this.panel.element.remove();
        }
        this.panel = undefined;
    }
}
/** Имя виджета */
CollectionWidget._alias = "Collection";

export { CollectionWidget };
